import { User } from "../../../features/user/types";
import {
  getEffectivePostStatuses,
  hasAuthoritativeBasicPostStatuses,
  isReadOnlyPostStatusCatalog,
} from "utils/post-statuses";

type StatusPermissionUser = Pick<User, "role" | "preferences"> &
  Partial<Pick<User, "postStatuses">>;

/**
 * Tooltip shown when a status option is disabled because the user lacks
 * permission to move a post into it. Shared across surfaces (status dropdown,
 * Approvals Board drag-and-drop, context menu) so the message stays consistent.
 */
export const STATUS_CHANGE_NOT_ALLOWED_TOOLTIP =
  "You are not allowed to change this post status.";

/**
 * Tooltip shown when a non-publishable status option is disabled because the
 * post-statuses (approvals) feature is not available on the current plan.
 * The select itself stays enabled so the user can still move the post to a
 * publishable status (e.g. after downgrading) rather than being stuck.
 */
export const STATUS_CHANGE_UPSELL_TOOLTIP =
  "This status isn't available on your current plan. Upgrade to use approval statuses.";

/**
 * SKE-4121: only a non-empty `canChangeStatusTo` restricts. Absent, null, empty
 * and malformed all mean unrestricted, matching the server's sole enforcement
 * point (`updateStatusOfMultiplePosts`).
 */
export const statusListAllows = (list: unknown, key: string): boolean =>
  !Array.isArray(list) || list.length === 0 || list.includes(key);

/**
 * Whether `user` is permitted to move a post into the approval status
 * identified by `statusKey`.
 */
export const canChangeToStatus = (
  user: StatusPermissionUser,
  statusKey: string
): boolean => {
  const statuses = hasAuthoritativeBasicPostStatuses(user as User)
    ? getEffectivePostStatuses(user as User)
    : null;
  if (statuses && isReadOnlyPostStatusCatalog(statuses)) return false;
  // Display aliases do not grant a different writable destination. The server
  // continues to enforce the raw permission list until migration repairs it.
  return (
    user.role !== "collaborator" ||
    statusListAllows(user.preferences?.canChangeStatusTo, statusKey)
  );
};

/**
 * Destination only: gating the source left a post in a disallowed status
 * unmovable, while the server accepted any permitted destination.
 */
export const canMovePostToStatus = (
  user: StatusPermissionUser,
  _currentStatusKey: string | null | undefined,
  targetStatusKey: string
): boolean => canChangeToStatus(user, targetStatusKey);
